import { z } from "zod";
/**
 * The shape the scraper produces, one per row of the source table.
 * Mirrors site-alerter-scraper-design.md exactly.
 */
export const breachDateSchema = z.object({ date: z.string() });
export const pdfLinkSchema = z.object({ name: z.string(), link: z.string() });
export const breachSchema = z.object({
    orgName: z.string(),
    breachDates: z.array(breachDateSchema),
    reportDates: z.array(breachDateSchema),
    pdfLinks: z.array(pdfLinkSchema),
    /**
     * The listing's own page for this record, where the sample notices live.
     * Deliberately outside `breachIdentity` — it is a pointer to the record, not
     * part of what makes the record distinct.
     */
    detailUrl: z.string().optional(),
});
/**
 * Identity is (orgName, breachDates, reportDates). pdfLinks are deliberately
 * excluded — the design doc is explicit that a changed or re-hosted PDF link
 * must not make an already-reported breach look new.
 */
function normalizeOrgName(orgName) {
    return orgName.trim().replace(/\s+/g, " ").toLowerCase();
}
function normalizeDates(dates) {
    // Sorted for determinism only. The source format ("12-01-25") does not sort
    // chronologically as a string, which does not matter: this ordering exists
    // so the same set of dates always produces the same key, nothing more.
    return dates
        .map((d) => d.date.trim())
        .filter((d) => d.length > 0)
        .sort();
}
/**
 * Stable canonical form of a breach's identity. Kept as a plain string with no
 * `node:crypto` dependency so this module stays importable from the browser
 * bundle; the worker hashes the result.
 */
export function breachIdentity(breach) {
    return JSON.stringify({
        orgName: normalizeOrgName(breach.orgName),
        breachDates: normalizeDates(breach.breachDates),
        reportDates: normalizeDates(breach.reportDates),
    });
}
/** Best-effort human label, used in log lines and email subjects. */
export function describeBreach(breach) {
    const reported = breach.reportDates[0]?.date;
    return reported ? `${breach.orgName.trim()} (reported ${reported})` : breach.orgName.trim();
}
//# sourceMappingURL=breach.js.map